/**
 * 다음(카카오) 우편번호 주소 검색 — 레거시 `findAddressButton.js` / `daumAddressInput.js` 포팅
 */
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Search } from 'lucide-react';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';

export interface DaumAddressResult {
  address: string;
  zonecode: string;
  roadAddress: string;
  jibunAddress: string;
}

interface AddressSearchButtonProps {
  onSelect: (result: DaumAddressResult) => void;
  buttonLabel?: string;
  disabled?: boolean;
}

const DAUM_SCRIPT_SRC =
  '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';

let daumScriptPromise: Promise<void> | null = null;

const loadDaumPostcodeScript = (): Promise<void> => {
  if (typeof window !== 'undefined' && window.daum?.Postcode) {
    return Promise.resolve();
  }
  if (daumScriptPromise) return daumScriptPromise;

  daumScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${DAUM_SCRIPT_SRC}"]`,
    );
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () =>
        reject(new Error('Daum postcode script load failed')),
      );
      return;
    }

    const script = document.createElement('script');
    script.src = DAUM_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Daum postcode script load failed'));
    document.head.appendChild(script);
  });

  return daumScriptPromise;
};

export const AddressSearchButton: React.FC<AddressSearchButtonProps> = ({
  onSelect,
  buttonLabel = '주소 찾기',
  disabled = false,
}) => {
  const containerId = useId().replace(/:/g, '');
  const [open, setOpen] = useState(false);
  const [scriptError, setScriptError] = useState('');
  const embeddedRef = useRef(false);

  const embedPostcode = useCallback(async () => {
    const container = document.getElementById(containerId);
    if (!container) return;

    try {
      await loadDaumPostcodeScript();
      if (!window.daum?.Postcode) {
        setScriptError('주소 검색 스크립트를 불러오지 못했습니다.');
        return;
      }

      container.innerHTML = '';
      embeddedRef.current = true;
      new window.daum.Postcode({
        oncomplete: (data) => {
          const address = data.roadAddress || data.address || '';
          onSelect({
            address,
            zonecode: data.zonecode ?? '',
            roadAddress: data.roadAddress ?? '',
            jibunAddress: data.jibunAddress ?? '',
          });
          setOpen(false);
        },
        width: '100%',
        height: '100%',
      }).embed(container);
    } catch {
      setScriptError('주소 검색을 시작할 수 없습니다.');
    }
  }, [containerId, onSelect]);

  useEffect(() => {
    if (!open) {
      embeddedRef.current = false;
      return;
    }
    const timer = window.setTimeout(() => {
      void embedPostcode();
    }, 50);
    return () => window.clearTimeout(timer);
  }, [embedPostcode, open]);

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() => {
          setScriptError('');
          setOpen(true);
        }}
      >
        <Search className="mr-2 h-4 w-4" />
        {buttonLabel}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>주소 검색</DialogTitle>
          </DialogHeader>
          {scriptError ? (
            <p className="text-sm text-destructive">{scriptError}</p>
          ) : null}
          <div
            id={containerId}
            className="h-[400px] w-full overflow-hidden rounded-12 border"
          />
        </DialogContent>
      </Dialog>
    </>
  );
};
